import { Link } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuthEpoch, getAuthUserId } from '@/lib/keepsake/epochs';
import { getContentOwnerBinding } from '@/lib/content-owner';
import {
  getContentPartition,
  listPrayerAttempts,
  getOwnerConflicts,
  getOwnerContentEntities,
  keepRemoteConflict,
  restoreConflictLoser,
} from '@/lib/content-lifecycle/store';
import {
  getCurrentContentLifecycleContext,
  syncContentLifecycle,
} from '@/lib/content-lifecycle/sync';
import type { ConflictRecordV1, ContentPartitionV1 } from '@/types/content-lifecycle';
import styles from './ContentLifecycleNotice.module.css';

const EMPTY_PARTITION: ContentPartitionV1 = {
  ownerId: '',
  frontier: 0,
  lastObservedHighWater: 0,
  legacyBootstrapComplete: false,
  heldReason: null,
  quarantineReason: null,
};

export default function ContentLifecycleNotice() {
  const { t } = useTranslation();
  const [partition, setPartition] = useState<ContentPartitionV1>(EMPTY_PARTITION);
  const [conflicts, setConflicts] = useState<ConflictRecordV1[]>([]);
  const [hasAttempts, setHasAttempts] = useState(false);
  const [entityHeld, setEntityHeld] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const ownerId = getAuthUserId();
    const authEpoch = getAuthEpoch();
    if (!ownerId || (await getContentOwnerBinding()) !== ownerId) {
      setPartition(EMPTY_PARTITION);
      setConflicts([]);
      setEntityHeld(false);
      setHasAttempts(false);
      return;
    }
    const [nextPartition, nextConflicts, entities, attempts] = await Promise.all([
      getContentPartition(ownerId),
      getOwnerConflicts(ownerId),
      getOwnerContentEntities(ownerId),
      listPrayerAttempts(ownerId),
    ]);
    if (getAuthUserId() !== ownerId || getAuthEpoch() !== authEpoch) return;
    setHasAttempts(attempts.length > 0);
    setPartition(nextPartition);
    setConflicts(nextConflicts);
    setEntityHeld(entities.some((entity) => entity.heldReason !== null));
  }, []);

  useEffect(() => {
    void refresh().catch(() => {});
    const handle = () => void refresh().catch(() => {});
    window.addEventListener('adia:sync-settled', handle);
    window.addEventListener('focus', handle);
    window.addEventListener('adia:prayer-attempts-changed', handle);
    window.addEventListener('pageshow', handle);
    return () => {
      window.removeEventListener('adia:sync-settled', handle);
      window.removeEventListener('focus', handle);
      window.removeEventListener('adia:prayer-attempts-changed', handle);
      window.removeEventListener('pageshow', handle);
    };
  }, [refresh]);

  const resolve = async (conflict: ConflictRecordV1, restore: boolean) => {
    const key = `${conflict.entityType}:${conflict.entityId}`;
    setBusyKey(key);
    try {
      const context = await getCurrentContentLifecycleContext();
      if (restore) {
        await restoreConflictLoser(context, conflict.entityType, conflict.entityId);
        await syncContentLifecycle();
      } else {
        await keepRemoteConflict(context, conflict.entityType, conflict.entityId);
      }
      await refresh();
    } finally {
      setBusyKey(null);
    }
  };

  const held = Boolean(partition.heldReason || partition.quarantineReason || entityHeld);
  if (!held && conflicts.length === 0 && !hasAttempts) return null;

  return (
    <aside className={styles.notice} aria-live="polite" aria-label={t('lifecycle.regionLabel')}>
      {hasAttempts && <Link to="/settings" className={styles.held}>
        <span className={styles.title}>{t('prayerRecovery.title')}</span>
        <span className={styles.body}>{t('prayerRecovery.body')}</span>
      </Link>}
      {held && (
        <div className={styles.held} role="status">
          <span className={styles.title}>{t('lifecycle.waitingTitle')}</span>
          <span className={styles.body}>{t('lifecycle.waitingBody')}</span>
        </div>
      )}
      {conflicts.map((conflict) => {
        const key = `${conflict.entityType}:${conflict.entityId}`;
        const busy = busyKey === key;
        return (
          <section className={styles.conflict} key={key}>
            <span className={styles.title}>{t('lifecycle.choiceTitle')}</span>
            <span className={styles.body}>
              {t(`lifecycle.choiceBody.${conflict.entityType}`)}
            </span>
            <div className={styles.actions}>
              <button type="button" disabled={busy} onClick={() => void resolve(conflict, false)}>
                {t('lifecycle.keepRemote')}
              </button>
              <button type="button" disabled={busy} onClick={() => void resolve(conflict, true)}>
                {busy ? t('lifecycle.resolving') : t('lifecycle.restoreMine')}
              </button>
            </div>
          </section>
        );
      })}
    </aside>
  );
}
